import { useEffect, useState } from "react";
import { Ban, Gift, Mail, Pencil, Printer, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, formatPrice } from "@/lib/api";
import { OrderDate, PAYMENT_LABELS, PAYMENT_STYLES } from "@/pages/admin/AdminDashboard";
import ReassignSchoolModal from "@/components/ReassignSchoolModal";
import { ChequeActions, ChequeSettingsCard } from "@/components/admin/ChequeAdmin";
import OrderEditDialog from "@/components/admin/OrderEditDialog";
import OrderCancelDialog from "@/components/admin/OrderCancelDialog";
import OrderAdjustments from "@/components/admin/OrderAdjustments";
import MondialRelayOrderInfo from "@/components/admin/MondialRelayOrderInfo";
import OrderContact from "@/components/admin/OrderContact";
import OrdersView, { ABANDON_DAYS, ORDER_TABS, orderCategory } from "@/components/admin/OrdersView";

const LOGO_URL = "https://customer-assets-lxgj4vgw.emergentagent.net/job_importer-tool/artifacts/ihudwvzv_Logo%20fleur-abeille.png";

const FULFILLMENT_OPTIONS = [
  { id: "preparation", label: "En préparation" },
  { id: "expediee", label: "Expédiée" },
  { id: "livree", label: "Livrée" },
];

const escapeHtml = (s) => String(s || "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const paymentLabel = (o) => {
  if (o.payment_method === "cheque") return "Chèque";
  if (o.payment_provider === "paypal") return "PayPal / carte";
  return (o.amount || 0) > 0 || o.payment_status !== "paid" ? "Carte bancaire" : "Points fidélité";
};


export default function AdminOrders() {
  const [orders, setOrders] = useState(null);
  const [reassignOrder, setReassignOrder] = useState(null);
  const [schools, setSchools] = useState([]);
  const [tab, setTab] = useState("active");
  const [editOrder, setEditOrder] = useState(null);
  const [cancelOrder, setCancelOrder] = useState(null);

  const openReassign = async (o) => {
    try {
      const { data } = await api.get("/admin/ecoles");
      setSchools(data.schools.filter((s) => s.active !== false));
      setReassignOrder(o);
    } catch { toast.error("Liste des écoles indisponible"); }
  };

  const submitReassign = async (f) => {
    try {
      const { data } = await api.post(`/admin/orders/${reassignOrder.session_id}/reassign-school`, f);
      toast.success(data.message);
      setReassignOrder(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const load = () => api.get("/admin/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
  useEffect(() => {
    load();
  }, []);

  const handleFulfillment = async (sessionId, status) => {
    try {
      await api.put(`/admin/orders/${sessionId}/fulfillment`, { fulfillment_status: status });
      setOrders((prev) => prev.map((o) => (o.session_id === sessionId ? { ...o, fulfillment_status: status } : o)));
      toast.success("Statut de préparation mis à jour");
    } catch {
      toast.error("Mise à jour impossible");
    }
  };

  const markQuoteHandled = async (sessionId) => {
    try {
      await api.put(`/admin/orders/${sessionId}/quote-handled`);
      setOrders((prev) => prev.map((o) => (o.session_id === sessionId ? { ...o, needs_quote: false } : o)));
      toast.success("Devis marqué comme traité");
    } catch {
      toast.error("Action impossible");
    }
  };

  const sendQuoteEmail = (order, gift) => {
    const to = order.customer_email || "";
    const subject = encodeURIComponent("Votre abonnement cadeau — Pétales de Miel : devis frais de port");
    const body = encodeURIComponent(
      `Bonjour,\n\nMerci pour votre commande d'abonnement cadeau à destination de ${gift.recipient_name} (${gift.city}, ${gift.country}).\n\n` +
      `La livraison étant hors de France, nous vous préparons un devis pour les frais de port. Nous revenons vers vous très vite avec le montant afin de finaliser l'envoi.\n\n` +
      `Belle journée,\nPétales de Miel`
    );
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  };

  const printGiftCard = (order, item) => {
    const g = item.gift || {};
    const sender = order.customer_email || "un proche";
    const html = `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Carte cadeau</title>
<style>
@page { size: A6; margin: 0; }
* { box-sizing: border-box; }
body { margin: 0; font-family: Georgia, 'Times New Roman', serif; color: #4a3b2a; }
.card { width: 148mm; height: 105mm; padding: 14mm 16mm; display: flex; flex-direction: column; background: #fdf8ef; }
.logo { height: 26mm; object-fit: contain; align-self: center; margin-bottom: 4mm; }
.for { font-size: 12pt; color: #b8860b; letter-spacing: 1px; text-transform: uppercase; }
.name { font-size: 20pt; font-weight: bold; margin: 2mm 0 5mm; }
.msg { font-size: 13pt; line-height: 1.5; flex: 1; white-space: pre-wrap; font-style: italic; }
.from { font-size: 11pt; text-align: right; margin-top: 4mm; }
.brand { text-align: center; font-size: 10pt; color: #b8860b; letter-spacing: 2px; margin-top: 3mm; }
</style></head><body>
<div class="card">
  <img class="logo" src="${LOGO_URL}" alt="Pétales de Miel" />
  <div class="for">Pour</div>
  <div class="name">${escapeHtml(g.recipient_name)}</div>
  <div class="msg">${escapeHtml(g.message) || "Une année de douceurs de la ruche, rien que pour vous."}</div>
  <div class="from">De la part de ${escapeHtml(sender)}</div>
  <div class="brand">PÉTALES DE MIEL</div>
</div>
<script>window.onload = function(){ setTimeout(function(){ window.print(); }, 300); };</script>
</body></html>`;
    const w = window.open("", "_blank", "width=640,height=480");
    if (!w) { toast.error("Autorisez les pop-ups pour imprimer la carte"); return; }
    w.document.write(html);
    w.document.close();
  };

  const closeAndReload = () => { setEditOrder(null); setCancelOrder(null); load(); };
  const reinstate = async (sid) => {
    try {
      await api.post(`/admin/orders/${sid}/reinstate`);
      toast.success(`Commande réintégrée pour ${ABANDON_DAYS} jours de plus`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Réintégration impossible");
    }
  };

  const renderOrder = (o) => (
  <div key={o.session_id} data-testid={`order-${o.session_id}`} className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <p className="text-xs uppercase tracking-[0.15em] text-stone-mute">
          <OrderDate value={o.created_at} /> · n° {o.session_id.slice(-8).toUpperCase()}
        </p>
        <p data-testid={`order-amount-${o.session_id}`} className="mt-1 flex flex-wrap items-center gap-x-2 font-serif text-xl font-semibold text-bark">
          {formatPrice(o.order_total_cents ?? Math.round((o.amount || 0) * 100))}
          <span data-testid={`order-payment-method-${o.session_id}`} className="rounded-full border border-amber-800/20 bg-white px-2.5 py-0.5 font-sans text-xs font-medium text-bark-soft">{paymentLabel(o)}</span>
          {o.payment_status === "paid" && o.order_total_cents != null && o.order_total_cents !== Math.round((o.amount || 0) * 100) && (
            <span className="ml-2 font-sans text-xs font-medium text-stone-mute">payé {formatPrice(Math.round((o.amount || 0) * 100))}</span>
          )}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {o.needs_quote && (
          <>
            <span data-testid={`order-quote-${o.session_id}`} className="flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
              Devis hors France à envoyer
            </span>
            <button
              data-testid={`order-quote-email-${o.session_id}`}
              onClick={() => sendQuoteEmail(o, (o.items || []).find((it) => it.gift && it.gift.is_gift)?.gift || {})}
              className="flex items-center gap-1.5 rounded-full border border-amber-800/25 bg-white px-3 py-1 text-xs font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep"
            >
              <Mail size={12} /> Envoyer le devis
            </button>
            <button
              data-testid={`order-quote-done-${o.session_id}`}
              onClick={() => markQuoteHandled(o.session_id)}
              className="rounded-full border border-sage/40 bg-sage-light px-3 py-1 text-xs font-medium text-sage transition-colors hover:bg-sage/20"
            >
              Marquer comme traité
            </button>
          </>
        )}
        {o.gift_wrap && (
          <span className="flex items-center gap-1.5 rounded-full bg-terra/10 px-3 py-1 text-xs font-medium text-terra">
            <Gift size={12} /> Emballage cadeau
          </span>
        )}
        <span data-testid={`order-payment-${o.session_id}`} className={`rounded-full px-3 py-1 text-xs font-medium ${PAYMENT_STYLES[o.payment_status] || "bg-sand text-bark-soft"}`}>
          {PAYMENT_LABELS[o.payment_status] || o.payment_status}
        </span>
        {o.payment_status === "awaiting_cheque" && <ChequeActions order={o} onDone={load} />}
        {o.payment_status === "awaiting_cheque" && orderCategory(o) === "abandoned" && (
          <button data-testid={`order-reinstate-${o.session_id}`} onClick={() => reinstate(o.session_id)} className="flex items-center gap-1.5 rounded-full bg-amber-deep px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-honey"><RotateCcw size={12} /> Réintégrer (délai postal)</button>
        )}
        {o.reassigned_to_school ? (
          <span data-testid={`order-reassigned-${o.session_id}`} className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">Réassignée à l'école {o.reassigned_to_school.school_name} — à remettre à l'école</span>
        ) : o.payment_status === "paid" && !["expediee", "livree"].includes(o.fulfillment_status) && (
          <button data-testid={`order-reassign-${o.session_id}`} onClick={() => openReassign(o)} className="rounded-full border border-amber-800/25 bg-white px-3 py-1 text-xs font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep">Réassigner à une école</button>
        )}
        {o.payment_status === "paid" && !o.reassigned_to_school && (
          <select
            data-testid={`order-fulfillment-${o.session_id}`}
            value={o.fulfillment_status || "preparation"}
            onChange={(e) => handleFulfillment(o.session_id, e.target.value)}
            className="rounded-full border border-amber-900/20 bg-cream px-3 py-1.5 text-xs font-medium text-bark outline-none focus:border-amber-deep"
          >
            {FULFILLMENT_OPTIONS.map((f) => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </select>
        )}
        {["paid", "awaiting_cheque"].includes(o.payment_status) && !o.reassigned_to_school && o.items?.length > 0 && (
          <button data-testid={`order-edit-${o.session_id}`} onClick={() => setEditOrder(o)} className="flex items-center gap-1.5 rounded-full border border-amber-800/25 bg-white px-3 py-1 text-xs font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep"><Pencil size={12} /> Modifier</button>
        )}
        {o.payment_status !== "cancelled" && !o.reassigned_to_school && (
          <button data-testid={`order-cancel-${o.session_id}`} onClick={() => setCancelOrder(o)} className="flex items-center gap-1.5 rounded-full border border-terra/30 bg-white px-3 py-1 text-xs font-medium text-terra transition-colors hover:bg-terra/10"><Ban size={12} /> Annuler</button>
        )}
      </div>
    </div>
    {o.items?.length > 0 && (
      <ul className="mt-4 space-y-1 border-t border-amber-900/10 pt-4 text-sm text-bark-soft">
        {o.items.map((item, idx) => (
          <li key={item.slot_id || item.box_label || `${item.product_id}-${idx}`} className="flex justify-between gap-4">
            <span>
              {item.quantity} × {item.name}
              {item.contenance && <span data-testid={`order-item-contenance-${o.session_id}-${idx}`} className="ml-1.5 rounded-full bg-sand px-2 py-0.5 text-xs font-medium text-bark">{item.contenance}</span>}
              {item.slot_label && <span className="block text-xs font-medium text-amber-deep">Créneau : {item.slot_label}</span>}
              {item.box_label && <span className="block text-xs font-medium text-amber-deep">{item.box_label}</span>}
              {item.subscription && <span className="block text-xs font-medium text-sage">Abonnement {item.subscription.cadence === "monthly" ? "mensuel" : "bimestriel"} · {item.subscription.deliveries} livraisons/an</span>}
              {item.gift && item.gift.is_gift && (
                <span className="mt-0.5 block rounded-lg bg-terra/10 px-2.5 py-1.5 text-xs text-bark">
                  🎁 Cadeau → <strong>{item.gift.recipient_name}</strong>, {item.gift.address}, {item.gift.postal_code} {item.gift.city}, {item.gift.country}
                  {item.gift.first_delivery_date && <span className="block">1ʳᵉ livraison : {item.gift.first_delivery_date}</span>}
                  {item.gift.message && <span className="block italic">« {item.gift.message} »</span>}
                  <button
                    type="button"
                    data-testid={`gift-card-print-${o.session_id}-${idx}`}
                    onClick={() => printGiftCard(o, item)}
                    className="mt-1.5 inline-flex items-center gap-1.5 rounded-full border border-amber-800/25 bg-white px-3 py-1 text-xs font-medium text-bark transition-colors hover:border-amber-deep hover:text-amber-deep"
                  >
                    <Printer size={12} /> Imprimer la carte
                  </button>
                </span>
              )}
              {item.cancelled && <span className="mt-0.5 inline-block rounded-full bg-terra/15 px-2.5 py-0.5 text-xs font-medium text-terra">Annulée</span>}
            </span>
            <span className="font-medium text-bark">{formatPrice(item.unit_amount_cents * item.quantity)}</span>
          </li>
        ))}
        <OrderContact order={o} />
        {o.association && (
          <li data-testid={`order-association-${o.session_id}`} className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-bark">
            <span className="font-semibold">Association : {o.association.name}</span> <span className="font-mono text-amber-deep">({o.association.code})</span>
            {o.association.pickup_address && <span className="block text-bark-soft">Retrait : {o.association.pickup_address}</span>}
            {o.association.postal && <span className="block text-bark-soft">Code postal client : {o.association.postal}</span>}
          </li>
        )}
        <MondialRelayOrderInfo order={o} onDone={load} />
        {o.shipping && (
          <li className="flex justify-between gap-4 text-stone-mute">
            <span>Livraison — {o.shipping.name}</span>
            <span>{o.shipping.charge_cents > 0 ? formatPrice(o.shipping.charge_cents) : "Offerte"}</span>
          </li>
        )}
      </ul>
    )}
    <OrderAdjustments order={o} onDone={load} />
  </div>
  );

  return (
    <div data-testid="admin-orders" className="space-y-6">
      <h1 className="font-serif text-3xl font-semibold text-bark">Commandes &amp; paiements</h1>
      <ChequeSettingsCard />
      <div className="flex flex-wrap gap-2">
        {ORDER_TABS.map(([id, label, cats]) => (
          <button key={id} data-testid={`orders-tab-${id}`} onClick={() => setTab(id)} className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === id ? "bg-amber-deep text-white" : "border border-amber-900/20 bg-white text-bark hover:border-amber-deep"}`}>
            {label} {orders && `(${orders.filter((o) => cats.includes(orderCategory(o))).length})`}
          </button>
        ))}
      </div>

      {!orders ? (
        <p data-testid="orders-loading" className="py-16 text-center font-serif text-xl text-bark-soft">Chargement…</p>
      ) : (
        <OrdersView tab={tab} orders={orders} renderOrder={renderOrder} />
      )}
      {editOrder && <OrderEditDialog order={editOrder} onClose={() => setEditOrder(null)} onSaved={closeAndReload} />}
      {cancelOrder && <OrderCancelDialog order={cancelOrder} onClose={() => setCancelOrder(null)} onDone={closeAndReload} />}
      {reassignOrder && <ReassignSchoolModal schools={schools} onSubmit={submitReassign} onClose={() => setReassignOrder(null)} />}
    </div>
  );
}
